import { useState } from 'react';

import Address from '@/components/Address';
import { Color } from '@/constants/color';
import useValidateAddress from '@/hooks/useValidateAddress';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import RadioGroup from '@/ui/components/Form/RadioGroup';
import Heading from '@/ui/components/Heading';
import Modal from '@/ui/components/Modal';
import Spinner from '@/ui/components/Spinner';

import { Props } from './types';

const ValidateAddress = ({ address, onModalClose, onSelect }: Props) => {
  const [selectedValidatedAddressBarCode, setSelectedValidatedAddressBarCode] = useState<
    string | null
  >(null);

  const {
    data: validatedAddresses,
    isError,
    error,
    isPending: isLoadingValidatedAddresses,
    refetch
  } = useValidateAddress(address);

  const handleSubmit = () => {
    const selectedAddress = validatedAddresses?.find(
      (address) => address.delivery_point_barcode === selectedValidatedAddressBarCode
    );

    if (!selectedAddress) {
      return;
    }

    onSelect(selectedAddress);
  };

  return (
    <div>
      <Modal
        dismissButton={{
          color: Color.WHITE,
          children: 'Cancel'
        }}
        button={{
          children: 'Submit',
          onClick: handleSubmit
        }}
        onClose={onModalClose}
        open={true}
        textContent={
          isLoadingValidatedAddresses ? (
            <Spinner label="Loading validated addresses" />
          ) : isError ? (
            <div>
              <Alert level="error">
                <div>{error?.message}</div>
                <Button color={Color.TEAL} onClick={() => refetch()} size="xs">
                  Retry
                </Button>
              </Alert>
            </div>
          ) : (
            <div>
              {validatedAddresses.every((address) => address.delivery_point_barcode === null) && (
                <div className="mb-2">
                  <Alert level="warn" message="No valid address found." />
                </div>
              )}

              <RadioGroup
                id="validated-addresses"
                selectedValue={selectedValidatedAddressBarCode}
                onChange={(value) => setSelectedValidatedAddressBarCode(value as string)}
                options={
                  validatedAddresses
                    ?.sort(
                      (a, b) => Number(a.delivery_point_barcode) - Number(b.delivery_point_barcode)
                    )
                    ?.map((validatedAddress) => ({
                      label: !validatedAddress.delivery_point_barcode ? (
                        <>
                          <Heading value={4} text="You entered:" />
                          <Address address={validatedAddress} showAddressFieldsOnly />
                        </>
                      ) : (
                        <Address address={validatedAddress} showAddressFieldsOnly />
                      ),
                      value: validatedAddress.delivery_point_barcode
                    })) || []
                }
              />
            </div>
          )
        }
        title="Choose a validated address"
      />
    </div>
  );
};

export default ValidateAddress;
